// Boostt AI · Brand Tokens v3.0
// Source of truth for all brand values in the MUI theme.
// Reference: docs/boostt-tokens-v3.css and docs/boostt-ai-brandbook-v3.md
//
// CSS variables are exported via src/app/styles/boostt-tokens.css (imported in layout.js).

// ─── Brand identity (theme-independent) ───────────────────────────
export const BRAND = {
  navy:        '#0D2430',
  teal:        '#1B8187',
  light:       '#58C3BE',
  yellow:      '#ECBF2B',
  yellowLight: '#FDF38E',
} as const;

// ─── Surfaces ─────────────────────────────────────────────────────
export const SURFACE = {
  light: {
    app:    '#F7FAFB',
    canvas: '#FFFFFF',
    hover:  '#EEF3F5',
    active: '#E4ECF0',
  },
  dark: {
    app:    '#0A1418',
    canvas: '#0F1B22',
    hover:  '#182832',
    active: '#1F3340',
  },
} as const;

// ─── Text hierarchy ────────────────────────────────────────────────
export const TEXT = {
  light: {
    primary:    '#0D2430',
    secondary:  '#3A4A52',
    tertiary:   '#6B7B82',
    quaternary: '#A3B0B6',
  },
  dark: {
    primary:    '#E8EEF0',
    secondary:  '#B8C5CA',
    tertiary:   '#7A8B92',
    quaternary: '#4C5A61',
  },
} as const;

// ─── Lines ────────────────────────────────────────────────────────
export const LINE = {
  light: {
    default: 'rgba(13, 36, 48, 0.10)',
    strong:  'rgba(13, 36, 48, 0.18)',
    accent:  'rgba(27, 129, 135, 0.28)',
  },
  dark: {
    default: 'rgba(232, 238, 240, 0.08)',
    strong:  'rgba(232, 238, 240, 0.16)',
    accent:  'rgba(27, 129, 135, 0.32)',
  },
} as const;

// ─── Accent (interactive only) ────────────────────────────────────
export const ACCENT = {
  light: {
    text:      '#1B8187',
    bg:        'rgba(27, 129, 135, 0.10)',
    bgStrong:  'rgba(27, 129, 135, 0.18)',
    border:    'rgba(27, 129, 135, 0.28)',
  },
  dark: {
    text:      '#1B8187',
    bg:        'rgba(27, 129, 135, 0.12)',
    bgStrong:  'rgba(27, 129, 135, 0.22)',
    border:    'rgba(27, 129, 135, 0.32)',
  },
} as const;

// ─── Status colors ────────────────────────────────────────────────
export const STATUS = {
  light: {
    success: { text: '#2D8B5F', bg: 'rgba(45, 139, 95, 0.10)',   border: 'rgba(45, 139, 95, 0.28)' },
    warning: { text: '#B8901E', bg: 'rgba(236, 191, 43, 0.14)',  border: 'rgba(184, 144, 30, 0.28)' },
    danger:  { text: '#C73A3A', bg: 'rgba(199, 58, 58, 0.10)',   border: 'rgba(199, 58, 58, 0.28)' },
  },
  dark: {
    success: { text: '#69D391', bg: 'rgba(105, 211, 145, 0.14)', border: 'rgba(105, 211, 145, 0.32)' },
    warning: { text: '#ECBF2B', bg: 'rgba(236, 191, 43, 0.14)',  border: 'rgba(236, 191, 43, 0.32)' },
    danger:  { text: '#EF6363', bg: 'rgba(239, 99, 99, 0.14)',   border: 'rgba(239, 99, 99, 0.32)' },
  },
} as const;

// ─── Data viz palette (8 categorical, color-blind aware) ──────────
// Always start with data1. Order matters — adjacent values are maximally distinguishable.
export const DATA_VIZ = {
  light: [
    { color: '#1B8187', bg: 'rgba(27, 129, 135, 0.10)',   label: 'teal' },
    { color: '#B8901E', bg: 'rgba(184, 144, 30, 0.10)',   label: 'amber' },
    { color: '#C45A47', bg: 'rgba(196, 90, 71, 0.10)',    label: 'coral' },
    { color: '#8B5CF6', bg: 'rgba(139, 92, 246, 0.10)',   label: 'plum' },
    { color: '#2563EB', bg: 'rgba(37, 99, 235, 0.10)',    label: 'sky' },
    { color: '#65A30D', bg: 'rgba(101, 163, 13, 0.10)',   label: 'lime' },
    { color: '#64748B', bg: 'rgba(100, 116, 139, 0.10)',  label: 'slate' },
    { color: '#A88862', bg: 'rgba(168, 136, 98, 0.10)',   label: 'sand' },
  ],
  dark: [
    { color: '#1B8187', bg: 'rgba(27, 129, 135, 0.18)',   label: 'teal' },
    { color: '#ECBF2B', bg: 'rgba(236, 191, 43, 0.18)',   label: 'amber' },
    { color: '#F08770', bg: 'rgba(240, 135, 112, 0.18)',  label: 'coral' },
    { color: '#C084FC', bg: 'rgba(192, 132, 252, 0.18)',  label: 'plum' },
    { color: '#7BB8E8', bg: 'rgba(123, 184, 232, 0.18)',  label: 'sky' },
    { color: '#A3E635', bg: 'rgba(163, 230, 53, 0.18)',   label: 'lime' },
    { color: '#94A3B8', bg: 'rgba(148, 163, 184, 0.18)',  label: 'slate' },
    { color: '#D4B896', bg: 'rgba(212, 184, 150, 0.18)',  label: 'sand' },
  ],
} as const;

// ─── Typography ────────────────────────────────────────────────────
export const FONT = {
  body:    "'Onest', 'Inter', -apple-system, BlinkMacSystemFont, sans-serif",
  display: "'Instrument Serif', 'Fraunces', Georgia, serif",
  mono:    "'JetBrains Mono', 'SF Mono', Consolas, monospace",
} as const;

// Google Fonts URL to add to index.html:
// https://fonts.googleapis.com/css2?family=Onest:wght@400;500;600;700&family=Instrument+Serif:ital@0;1&family=JetBrains+Mono:wght@400;500;600&display=swap

// ─── Type scale ────────────────────────────────────────────────────
export const TYPE_SCALE = {
  display: { size: 'clamp(36px, 5vw, 64px)', lineHeight: 1.05, letterSpacing: '-0.02em' },
  h1:      { size: '26px',  weight: 700, letterSpacing: '-0.01em' },
  h2:      { size: '18px',  weight: 700, letterSpacing: '-0.005em' },
  h3:      { size: '16px',  weight: 600 },
  body:    { size: '14px',  lineHeight: 1.5 },
  sm:      { size: '13px' },
  caption: { size: '12px' },
  micro:   { size: '10px',  weight: 700, letterSpacing: '0.06em' },
  metric:  { size: '36px',  lineHeight: 1.1, letterSpacing: '-0.02em' },
} as const;

// ─── Spacing (4px base grid) ───────────────────────────────────────
export const SPACE = {
  1:  4,
  2:  8,
  3:  12,
  4:  16,
  5:  20,
  6:  24,
  7:  28,
  8:  32,
  10: 40,
  12: 48,
  16: 64,
  20: 80,
} as const;

// ─── Border radius ────────────────────────────────────────────────
export const RADIUS = {
  xs:   4,
  sm:   6,
  md:   10,
  lg:   14,
  xl:   20,
  full: 999,
} as const;

// ─── Motion ───────────────────────────────────────────────────────
export const MOTION = {
  duration: {
    instant: 60,
    fast:    120,
    base:    220,
    slow:    420,
    glacial: 800,
  },
  easing: {
    default: 'cubic-bezier(0.32, 0.72, 0.36, 1.0)',
    spring:  'cubic-bezier(0.34, 1.56, 0.64, 1.0)',
  },
  staggerInterval: 40,
} as const;

// ─── z-index architecture (6-layer stack) ─────────────────────────
export const Z = {
  tooltip:  50,
  dropdown: 100,
  modal:    1000,
  drawer:   1001,
  popover:  1099,
  palette:  1100,
  toast:    2000,
} as const;

// ─── Layout ───────────────────────────────────────────────────────
export const LAYOUT = {
  navWidth:          260,
  navWidthCollapsed: 64,
  topbarHeight:      56,
  modalSm:           420,
  modalMd:           560,
  modalLg:           720,
  drawerWidth:       440,
  toastMaxWidth:     400,
  dropdownMinWidth:  200,
  paletteMaxWidth:   560,
} as const;

// ─── Responsive breakpoints ───────────────────────────────────────
export const BREAKPOINTS = {
  mobile:  640,   // <= 640px: 1-col stacked, horizontal bottom nav
  tablet:  1024,  // <= 1024px: collapsed 64px nav rail, 2-col or 1-col
  desktop: 1025,  // > 1024px: full 260px nav rail, multi-column grids
} as const;

// ─── Density multipliers ──────────────────────────────────────────
export const DENSITY = {
  compact:     0.75,
  default:     1.0,
  comfortable: 1.25,
} as const;

// ─── Shadows ──────────────────────────────────────────────────────
// Removed 2026-09-17: this duplicated theme.customShadows / --shadow-1/2/3
// (src/app/styles/boostt-tokens.css) with identical values under a second name.
// Use var(--shadow-1|2|3) directly, or theme.customShadows.* for MUI-native cases.
